import { Enum } from '@lighthouse/tools'
import type { MakeADT } from 'ts-adt/MakeADT'

import type { FieldBlockAbstract, OnOffBlockAbstract } from '../abstracts'
import { type FieldOptionsProtocol } from '../abstracts'
import type { BlockRuntimeContext, FieldBreakPointProtocol } from './blocks'
import type { ButtonConfigure } from './button'
import type { DTFile, FILE_TYPE_CATEGORY_ENUM, ScannerType } from './data'
import type { BackgroundProtocol } from './design'
import type { EventsProtocol } from './event'
import type { FieldType } from './field'
import type { FilterFormType } from './filter'
import type { MultipleChoiceProtocol, SortableProtocol } from './general'
import type { IconDecorationProtocol } from './icon'
import type { AlignMent, Direction } from './layout'
import type { RichTextContentProtocol } from './richtext'
import type { ViewField } from './view'

export const fieldBlockInput = [
    'text',
    'number',
    'email',
    'url',
    'phoneNumber',
    'person',
    'date',
    'calendar',
    'relativeSelect',
    'cascade',
    'file',
    'notes',
    'checkbox',
    // 'select',
    'slider'
] as const

export type FieldInputType = typeof fieldBlockInput[number]

export type DateRange = 'ALL' | 'BEFORE_TODAY' | 'AFTER_TODAY' | 'AFTER_CUSTOM' | 'DATASOURCE_CUSTOM'

export type NumberConfig = {
    number?: {
        min?: number
        max?: number
    }
} & FieldOptionsProtocol

export type PhoneNumberConfig = {
    //
    phoneNumber?: {
        areaCode: string

        isOpened: boolean

        isUsedAreaCode?: boolean

        selectAreaCode?: string[]
    }
} & FieldOptionsProtocol

export type UrlConfig = {
    //
} & FieldOptionsProtocol

export type EmailConfig = {
    //
} & FieldOptionsProtocol

export type TextConfig = {
    text?: {
        minFontNumber?: number
        maxFontNumber?: number
        regular?: string
        isRegular?: boolean
        regularErrorLabel?: string
        disableInputs?: ('input' | 'album')[]
        scannerList?: ScannerType[]
        enableWxScanner?: boolean
    }
} & FieldOptionsProtocol &
    Partial<FieldBreakPointProtocol>

// export type SelectConfig = {
//     //
//     select?: MultipleChoiceProtocol
// } & FieldOptionsProtocol

export type DateConfig = {
    date?: {
        dateFormat?: string
        range?: DateRange
        customDays?: number
    }
} & FieldOptionsProtocol &
    Partial<FieldBreakPointProtocol>

export const CALENDAR_MODE = Enum('week', 'month')
export type CalendarMode = Enum<typeof CALENDAR_MODE>

export const CALENDAR_TIME_ZONE = Enum('local', 'Asia/Shanghai')
export type CalendarTimeZone = Enum<typeof CALENDAR_TIME_ZONE>

export type CalendarConfig = {
    calendar?: {
        /**
         * for compatible, if value is empty, the value will be month
         * @default month
         */
        mode?: CalendarMode
        /** 缺省与 local 按本机时区。Asia/Shanghai 按北京时间读写 */
        timeZone?: CalendarTimeZone
        dateFormat?: string
        range?: DateRange
        customDays?: number
        rangeDsId?: string
        rangeFieldId?: string
        rangeFilter?: FilterFormType

        enabled: boolean
        describeDsId: string
        filter?: FilterFormType
        describeFieldId: string
        describeTextFieldId: string
    }
} & FieldOptionsProtocol

export enum FIELD_TYPE_LIMIT_ENUM {
    'ALL' = 'ALL', // 不限制
    'TYPE' = 'TYPE', // 按类型
    'SUFFIX' = 'SUFFIX' // 按后缀
}

export const FILE_MODE = Enum('list', 'thumbnail', 'grid')
export type FileMode = Enum<typeof FILE_MODE>

export type FileConfig = {
    file: {
        mode: FileMode
        enableSortable?: boolean // 是否允许对已上传文件进行拖拽排序
        uploadLengthMinLimit?: number // 文件数量限制(最小) 最小值0, 没有设置默认0
        uploadLengthMaxLimit?: number // 文件数量限制(最大)  最大值100, 没有设置默认100
        fileLimitType: FIELD_TYPE_LIMIT_ENUM
        fileType?: FILE_TYPE_CATEGORY_ENUM // 当fileLimitType为TYPE时，fileType为必填
        fileSuffix?: string // 当fileLimitType为SUFFIX时，fileSuffix为必填
        // 单个文件大小限制, 单位MB
        fileSizeLimit?: number
    }
} & FieldOptionsProtocol

export type NotesConfig = {
    //
} & FieldOptionsProtocol

export type PersonConfig = {
    person?: {
        filter?: FilterFormType
    } & MultipleChoiceProtocol
} & FieldOptionsProtocol &
    Partial<FieldBreakPointProtocol>

export enum CheckboxShapeEnum {
    'collect' = 'collect',
    'heart' = 'heart',
    'flag' = 'flag',
    'like' = 'like',
    'checkbox' = 'checkbox'
}

export type CheckboxConfig = {
    //
    checkbox?: {
        checkedIcon?: {
            icon: IconDecorationProtocol
            color: BackgroundProtocol
        }
        unCheckedIcon?: {
            icon: IconDecorationProtocol
            color: BackgroundProtocol
        }
        type?: 'switch' | 'checkbox' | 'custom'
        // 老版
        shape?: CheckboxShapeEnum
        gapType?: 'auto' | 'fixed'
        gap?: number
        describe?: RichTextContentProtocol
        describePosition?: 'left' | 'right'
    }
} & FieldOptionsProtocol

export type SliderConfig = {
    slider?: {
        /** 	Minimal possible value, 0 by default */
        min: number

        /** Maximum possible value, 100 by default */
        max: number

        /** 通过拇指拖动和箭头来增加/减少值的数字，默认为 1 */
        step: number

        /** 是否启用输入框组件 */
        isInput: boolean

        // /** 颜色模式 */
        // colorMode: 'theme' | 'custom'

        // /** 滑块颜色 */
        // color: string
    }
} & FieldOptionsProtocol

export type RelativeSelectConfig = {
    relativeSelect?: {
        relativePointer?: string
        relativeFieldPointer?: string
        relativeShowFieldPointer?: string
        showType: 'table' | 'list'
        filter?: FilterFormType
        disabledFilter?: FilterFormType
        viewFieldSettings: ViewField[]
        // viewPrimaryField?: string
        direction: Direction
        align: AlignMent
        showMode?: 'input' | 'checkbox' | 'tag'
        showId?: string

        addButton?: Pick<ButtonConfigure, 'iconColor' | 'color' | 'iconPosition' | 'icon' | 'showType'> & {
            name: string
            enable: boolean
        } & EventsProtocol
    } & MultipleChoiceProtocol &
        SortableProtocol
} & FieldOptionsProtocol

export type CascadeConfig = {
    cascade?: {
        // 选项来源
        cascadePointer?: string
        // 选项名
        cascadeFieldPointer?: string
        // 显示字段
        cascadeShowFieldPointer?: string
        // 父层级
        parentFieldPointer?: string
        // 排序字段
        sortFieldPointer?: string
        // 筛选数据
        filter?: FilterFormType
        // 结果显示层级路径
        showPath?: boolean
        // 必须选到最终层级
        isLastLevel?: boolean
    }
} & FieldOptionsProtocol

export type FieldInputConfigProtocol = MakeADT<
    'inputType',
    {
        text: TextConfig
        number: NumberConfig
        email: EmailConfig
        phoneNumber: PhoneNumberConfig
        url: UrlConfig
        // select: SelectConfig
        date: DateConfig
        file: FileConfig
        notes: NotesConfig
        relativeSelect: RelativeSelectConfig
        person: PersonConfig
        checkbox: CheckboxConfig
        slider: SliderConfig
        cascade: CascadeConfig
        calendar: CalendarConfig
    }
>

export type SimpleInputValue = {
    value: string
}

export type CascadeInputValue = {
    value: string | string[]
}

export type NumberInputValue = {
    value: string | number
}

export type SelectInputValue = {
    value: string[]
}

export type DateInputValue = {
    value: number | string
}

export type FileInputValue = {
    value: DTFile[]
}

export type NotesInputValue = {
    value: RichTextContentProtocol
}

export type RelativeSelectInputValue = {
    value: string | string[]
}

export type PersonInputValue = {
    value: string[]
}

export type CheckboxInputValue = {
    value: boolean
}

export type FieldInputADTValue = MakeADT<
    'type',
    {
        text: SimpleInputValue
        number: NumberInputValue
        email: SimpleInputValue
        phoneNumber: SimpleInputValue
        url: SimpleInputValue
        // select: SelectInputValue
        date: DateInputValue
        file: FileInputValue
        notes: NotesInputValue
        person: PersonInputValue
        relativeSelect: RelativeSelectInputValue
        checkbox: CheckboxInputValue
        slider: NumberInputValue
        cascade: CascadeInputValue
        calendar: DateInputValue
    }
>

export type InputValueItem = FieldInputADTValue & {
    id: string
    fieldId: string
    dsId?: string
    isCheckRepeat?: boolean
    fieldType?: FieldType
    scope?: string
    viewId?: string
    source: 'form' | 'field' | 'subForm'
    code?: string
}

export type FormInputItem = {
    id: string
} & FieldInputConfigProtocol

export type FormOptions = {
    pointer: string

    submitButtonTitle: string

    inputList: FormInputItem[]
}

/**
 * 可用的 field/onOff block 及其上下文信息
 * 使用 BlockRuntimeContext 泛型，限定 block 类型为 FieldBlockAbstract | OnOffBlockAbstract
 */
export type AvailableFieldBlockInfo = BlockRuntimeContext<FieldBlockAbstract | OnOffBlockAbstract>
